import { getCardStatus, getUserCards, getCreditInformation } from "../api/api";
import { useCardStatusStore } from "../stores/cardStatusStore";
import { useCreditStore } from "../stores/creditStore";
import { useSDKConfigStore } from "../stores/sdkConfigStore";

class DataFetcher {
  private static instance: DataFetcher;
  private hasFetched = false;

  static getInstance(): DataFetcher {
    if (!DataFetcher.instance) {
      DataFetcher.instance = new DataFetcher();
    }
    return DataFetcher.instance;
  }

  async fetchAllData(): Promise<void> {
    const { eoaAddress, isInitialized } = useSDKConfigStore.getState();

    if (!isInitialized || !eoaAddress || this.hasFetched) {
      return;
    }

    console.log("🚀 SDK: Starting data fetch for:", eoaAddress);

    // Get store references
    const cardStore = useCardStatusStore.getState();
    const creditStore = useCreditStore.getState();

    // Set loading states
    cardStore._setLoading(true);
    creditStore._setLoading(true);

    try {
      // Fetch all data in parallel
      const [
        cardStatusResponse,
        userCardsResponse,
        aaveCreditResponse,
        eulerCreditResponse,
      ] = await Promise.all([
        getCardStatus(eoaAddress),
        getUserCards(eoaAddress),
        getCreditInformation(eoaAddress, "aave-v3"),
        getCreditInformation(eoaAddress, "euler"),
      ]);

      // Update card data
      const userCard = userCardsResponse.data?.[0] || null;
      cardStore._setCardStatus(cardStatusResponse.data, userCard);

      // Update credit data
      creditStore._setCreditInfo("aave", aaveCreditResponse);
      creditStore._setCreditInfo("euler", eulerCreditResponse);

      this.hasFetched = true;
      console.log("✅ SDK: Successfully fetched all data for:", eoaAddress);
    } catch (error) {
      console.error("❌ SDK: Error fetching data:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch data";

      cardStore._setError(errorMessage);
      creditStore._setError(errorMessage);
    } finally {
      cardStore._setLoading(false);
      creditStore._setLoading(false);
    }
  }

  // Public method to trigger data fetching after authentication
  async fetchDataAfterAuth(): Promise<void> {
    // Reset the fetch flag to allow fetching again
    this.hasFetched = false;
    await this.fetchAllData();
  }

  reset(): void {
    this.hasFetched = false;
    useCardStatusStore.getState()._reset();
    useCreditStore.getState()._reset();
  }
}

export const dataFetcher = DataFetcher.getInstance();
